export type PlainJsStoreOrmData<StoreRecord extends object, IDType extends number | string> = {
    byId: Record<IDType, StoreRecord>;
    allIds: IDType[];
};

export const ID_FIELD = '_id';

class IdGeneratorClass {
    prefix_counters: Record<string, number> = {};

    generateId(prefix: string): string {
        if (!Object.prototype.hasOwnProperty.call(this.prefix_counters, prefix)) {
            this.prefix_counters[prefix] = 0;
        }

        const id = `${prefix}-${this.prefix_counters[prefix]}`;
        this.prefix_counters[prefix]++;

        return id;
    }
}

export const IdGenerator = new IdGeneratorClass();

/**
 * The PlainJsStoreORM is an equivalent to the StoreORM class, but it is intended for use with plain
 * JS objects rather than ImmutableJS objects. This is useful for slices, which do not play nicely with
 * ImmutableJS.
 */
export default class PlainJsStoreORM<StoreRecord extends object, IDType extends number | string> {
    _data: PlainJsStoreOrmData<StoreRecord, IDType>;

    constructor(data: PlainJsStoreOrmData<StoreRecord, IDType>) {
        this._data = data;
    }

    static createEmpty<StoreRecord extends object, IDType extends number | string>() {
        return new PlainJsStoreORM<StoreRecord, IDType>({
            byId: {} as Record<IDType, StoreRecord>,
            allIds: [],
        });
    }

    getByIdOrThrow(id: IDType): StoreRecord {
        const item = this._data.byId[id];
        if (!item) {
            throw new Error(`Item with id ${id} not found`);
        }
        return item;
    }

    getById(id: IDType): StoreRecord | undefined {
        return this._data.byId[id];
    }

    getByIndex(index: number): StoreRecord | undefined {
        const id = this._data.allIds[index];
        return id === undefined ? undefined : this.getById(id);
    }

    contains(id: IDType): boolean {
        return this._data.byId[id] !== undefined;
    }

    indexOf(id: IDType): number {
        return this._data.allIds.indexOf(id);
    }

    isEmpty(): boolean {
        return this.length === 0;
    }

    pushItem(item: StoreRecord, idField: keyof StoreRecord) {
        const id = this._idOf(item, idField);
        const isNew = !this._data.allIds.includes(id);
        if (!isNew && this._data.byId[id] === item) {
            return;
        }
        this._data = {
            ...this._data,
            byId: {
                ...this._data.byId,
                [id]: item,
            },
            allIds: isNew ? [...this._data.allIds, id] : this._data.allIds,
        };
    }

    pushItemToFront(item: StoreRecord, idField: keyof StoreRecord) {
        const id = this._idOf(item, idField);
        this._data = {
            ...this._data,
            byId: {
                ...this._data.byId,
                [id]: item,
            },
            allIds: [id, ...this._data.allIds.filter((existingId) => existingId !== id)],
        };
    }

    insertItemAt(index: number, item: StoreRecord, idField: keyof StoreRecord) {
        const id = this._idOf(item, idField);
        const allIds = this._data.allIds.filter((existingId) => existingId !== id);
        allIds.splice(Math.min(index, allIds.length), 0, id);
        this._data = {
            ...this._data,
            byId: {
                ...this._data.byId,
                [id]: item,
            },
            allIds,
        };
    }

    updateById(id: IDType, updater: (item: StoreRecord) => StoreRecord) {
        const item = this.getByIdOrThrow(id);
        const updatedItem = updater(item);
        if (updatedItem === item) {
            return;
        }
        this._data = {
            ...this._data,
            byId: {
                ...this._data.byId,
                [id]: updatedItem,
            },
        };
    }

    updateAll(updater: (item: StoreRecord) => StoreRecord) {
        this._data.allIds.forEach((id) => this.updateById(id, updater));
    }

    delete(ids: readonly IDType[]) {
        const idsToDelete = new Set(ids.filter((id) => this.contains(id)));
        if (idsToDelete.size === 0) {
            return;
        }
        const byId = { ...this._data.byId };
        idsToDelete.forEach((id) => delete byId[id]);
        this._data = {
            ...this._data,
            byId,
            allIds: this._data.allIds.filter((id) => !idsToDelete.has(id)),
        };
    }

    map<T>(mapper: (item: StoreRecord, index: number) => T): T[] {
        return this._data.allIds.map((id, index) => mapper(this.getByIdOrThrow(id), index));
    }

    forEach(callback: (item: StoreRecord, index: number) => void) {
        this._data.allIds.forEach((id, index) => callback(this.getByIdOrThrow(id), index));
    }

    filter(predicate: (item: StoreRecord, index: number) => boolean): StoreRecord[] {
        return this.map((item) => item).filter(predicate);
    }

    find(predicate: (item: StoreRecord, index: number) => boolean): StoreRecord | undefined {
        return this.map((item) => item).find(predicate);
    }

    _idOf(item: StoreRecord, idField: keyof StoreRecord): IDType {
        const id = item[idField];
        if (typeof id !== 'number' && typeof id !== 'string') {
            throw new Error('idField must be a number or string');
        }
        return id as IDType;
    }

    get allIds(): IDType[] {
        return this._data.allIds;
    }

    get length(): number {
        return this._data.allIds.length;
    }

    get data(): PlainJsStoreOrmData<StoreRecord, IDType> {
        return this._data;
    }
}
